"use client";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { jsx, jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
//#region src/mermaid-diagram.tsx
/**
* Spec 59 — markdown viewer: mermaid diagrams.
*
* Server-rendered `markdown-text.tsx` detects ``` mermaid ``` fenced
* code blocks and emits this component in their place. We render
* client-side because mermaid's parser + SVG generator weighs ~500KB
* and we don't want it in every chat page chunk by default — the
* code only loads when at least one mermaid block exists on the page.
*
* Failure mode: a syntactically-broken diagram surfaces as a
* destructive-tinted card with the parser error + the raw source
* (collapsed), not a thrown render. Customers expect "their code"
* to come back if mermaid can't parse it.
*/
const DENSITY = {
	compact: {
		error: "my-3 p-3 text-xs",
		icon: "h-3 w-3",
		detail: "text-[11px]",
		placeholder: "my-3 h-24 text-xs",
		frame: "my-3 p-3"
	},
	comfortable: {
		error: "my-6 p-4 text-sm",
		icon: "h-3.5 w-3.5",
		detail: "text-xs",
		placeholder: "my-6 h-32 text-sm",
		frame: "my-6 flex justify-center p-4 [&_svg]:h-auto"
	}
};
let mermaidInitialized = false;
function MermaidDiagram({ source, density = "compact" }) {
	const d = DENSITY[density];
	const containerRef = useRef(null);
	const [error, setError] = useState(null);
	const [svg, setSvg] = useState(null);
	useEffect(() => {
		let cancelled = false;
		const render = async () => {
			try {
				const { default: mermaid } = await import("mermaid");
				if (!mermaidInitialized) {
					mermaid.initialize({
						startOnLoad: false,
						theme: "neutral",
						securityLevel: "strict",
						fontFamily: "inherit"
					});
					mermaidInitialized = true;
				}
				const id = `mermaid-${Math.random().toString(36).slice(2, 10)}`;
				const { svg: rendered } = await mermaid.render(id, source);
				if (cancelled) return;
				setSvg(rendered);
				setError(null);
			} catch (err) {
				if (cancelled) return;
				setError(err instanceof Error ? err.message : "Diagram failed to render.");
				setSvg(null);
			}
		};
		render();
		return () => {
			cancelled = true;
		};
	}, [source]);
	if (error) return /* @__PURE__ */ jsxs("div", {
		className: `space-y-2 rounded-inset border border-destructive/30 bg-destructive/5 ${d.error}`,
		children: [
			/* @__PURE__ */ jsxs("p", {
				className: "flex items-center gap-1.5 font-medium text-destructive",
				children: [/* @__PURE__ */ jsx(FontAwesomeIcon, {
					icon: faTriangleExclamation,
					className: d.icon
				}), "Diagram failed to render"]
			}),
			/* @__PURE__ */ jsx("p", {
				className: `font-mono text-muted-foreground break-words ${d.detail}`,
				children: error
			}),
			/* @__PURE__ */ jsxs("details", { children: [/* @__PURE__ */ jsx("summary", {
				className: `cursor-pointer text-muted-foreground hover:text-foreground ${d.detail}`,
				children: "Show source"
			}), /* @__PURE__ */ jsx("pre", {
				className: `mt-1.5 overflow-x-auto rounded-inset bg-background/60 p-2 font-mono ${d.detail}`,
				children: source
			})] })
		]
	});
	if (!svg) return /* @__PURE__ */ jsx("div", {
		ref: containerRef,
		className: `flex items-center justify-center rounded-inset border border-dashed bg-muted/20 text-muted-foreground ${d.placeholder}`,
		children: "Rendering diagram…"
	});
	return /* @__PURE__ */ jsx("div", {
		className: `overflow-x-auto rounded-inset border bg-background [&_svg]:max-w-full ${d.frame}`,
		dangerouslySetInnerHTML: { __html: svg }
	});
}
//#endregion
export { MermaidDiagram };
